import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2 } from "lucide-react";

const API_ROOT = process.env.REACT_APP_BACKEND_URL;

const FENCE_OPTIONS = [
  { value: "chain_wire", label: "Chain Wire" },
  { value: "colorbond", label: "Colorbond" },
  { value: "aluminium_standard", label: "Aluminium (standard)" },
  { value: "aluminium_custom", label: "Aluminium (custom)" },
  { value: "tubular", label: "Tubular Steel" },
  { value: "glass_pool", label: "Glass Pool Fencing" },
  { value: "timber", label: "Timber" },
  { value: "gates_automation", label: "Gates & Automation" },
  { value: "other", label: "Not sure / Other" },
];

const EMPTY = { name: "", phone: "", email: "", suburb: "", fence_type: "other", message: "", website: "" };

export default function PublicEnquiry() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API_ROOT}/api/public/enquiry`, {
        ...form,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        suburb: form.suburb.trim() || null,
        message: form.message.trim() || null,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — please call us on 02 4324 1441");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-3 mb-6">
          <img src="/ccfi-logo.png?v=4" alt="CCFI" className="w-14 h-14 object-contain" />
          <div>
            <div className="font-display font-bold text-white text-lg">Central Coast Fencing Industries</div>
            <div className="text-[11px] text-cyan-400 stamp-label">Free Quote Enquiry</div>
          </div>
        </div>

        <div className="bg-white rounded-sm p-6 sm:p-8 industrial-shadow">
          {done ? (
            <div className="text-center py-10" data-testid="enquiry-success">
              <CheckCircle2 size={48} className="text-emerald-500 mx-auto mb-4" />
              <h2 className="font-display text-2xl font-semibold text-slate-900">Thanks — we've got it!</h2>
              <p className="text-slate-600 mt-2 text-sm">Your enquiry is with our team now. We'll call you back shortly to organise your free quote.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="public-enquiry-form">
              <h1 className="font-display text-2xl font-semibold text-slate-900">Get a free fencing quote</h1>
              <p className="text-sm text-slate-500 -mt-2">Tell us what you need — we'll call you back.</p>
              <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="stamp-label text-slate-700">Your name *</Label>
                  <Input data-testid="enquiry-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm h-11" placeholder="John Smith" />
                </div>
                <div>
                  <Label className="stamp-label text-slate-700">Phone</Label>
                  <Input data-testid="enquiry-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-sm h-11" placeholder="0400 000 000" inputMode="tel" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="stamp-label text-slate-700">Email</Label>
                  <Input data-testid="enquiry-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.replace(/\s+/g, "") })} className="mt-1 rounded-sm h-11" placeholder="you@email.com" autoCapitalize="none" autoCorrect="off" />
                </div>
                <div>
                  <Label className="stamp-label text-slate-700">Suburb</Label>
                  <Input data-testid="enquiry-suburb" value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} className="mt-1 rounded-sm h-11" placeholder="Erina" />
                </div>
              </div>
              <div>
                <Label className="stamp-label text-slate-700">Fence type</Label>
                <Select value={form.fence_type} onValueChange={(v) => setForm({ ...form, fence_type: v })}>
                  <SelectTrigger data-testid="enquiry-fence-type" className="mt-1 rounded-sm h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FENCE_OPTIONS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="stamp-label text-slate-700">Tell us about the job</Label>
                <Textarea data-testid="enquiry-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 rounded-sm" rows={3} placeholder="Approx. metres, gates needed, timeframe…" />
              </div>
              <Button data-testid="enquiry-submit" type="submit" disabled={sending || !form.name.trim() || (!form.phone.trim() && !form.email.trim())} className="w-full h-12 rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white font-semibold">
                {sending ? <Loader2 className="animate-spin" size={18} /> : <><Send size={16} className="mr-2" /> Send enquiry</>}
              </Button>
              <p className="text-[11px] text-slate-400 text-center">Provide a phone number or email so we can get back to you.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
